import './ListEntryContent.css';

import { useMemo, useState } from 'react';

import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';

import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { useModalKeyboard } from '../../../../../hooks/shortcuts/useModalKeyboard';
import { EntryErrorList } from '../../../../../types/types';
import InputLabel from '../../../../Manager/InputLabel/InputLabel';
import { formatEntryListCell } from '../../utils/formatEntryListCell';
import { getEntryListReturnValue } from '../../utils/getEntryListReturnValue';
import EntryActionButtons from '../EntryActionButtons/EntryActionButtons';

export interface ListEntryContentProps {
  list?: EntryErrorList;
  isSubmitting: boolean;
  handleAction: (action: () => void | Promise<void>) => void | Promise<void>;
  submitEntryValue: (value: string) => void | Promise<void>;
  cancelModalAction: () => void | Promise<void>;
}

// DataTable rows must be objects — wraps the raw value array with its index.
type EntryListRow = {
  __index: number;
  __row: (string | number | null)[];
};

const ListEntryContent = ({
  list,
  isSubmitting,
  handleAction,
  submitEntryValue,
  cancelModalAction,
}: ListEntryContentProps) => {
  // Clicking a row only selects it; the value is sent via the confirm button.
  const [selectedRow, setSelectedRow] = useState<EntryListRow | null>(null);

  const { t } = useTranslate();
  // Only the inlined 'data' variant is supported — the 'sql' variant needs the
  // query executed on the server, which this client has no endpoint for.
  const rows = list?.data;
  const fields = list?.fields ?? [];
  const displayFields = fields.filter((field) => field.label);

  const rowObjects: EntryListRow[] = useMemo(
    () => (rows ?? []).map((row, index) => ({ __index: index, __row: row })),
    [rows]
  );

  const submit = () => {
    if (selectedRow === null) {
      return;
    }

    handleAction(() =>
      submitEntryValue(getEntryListReturnValue(selectedRow.__row, fields))
    );
  };

  useModalKeyboard({
    onEnter: selectedRow === null ? undefined : submit,
    onEscape: () => handleAction(cancelModalAction),
  });

  return (
    <div className="list-entry-content">
      {rows ? (
        // grid-header-visible: Grid.css hides .p-datatable-thead until the
        // dataset scroll coordinator adds this class; here it is static.
        <div className="list-entry-table-wrapper grid-header-visible">
          <DataTable
            value={rowObjects}
            // 'grid' scopes the dataset grid styling from Grid.css onto this table.
            className="grid list-entry-grid"
            selectionMode="single"
            selection={selectedRow}
            onSelectionChange={(e) => {
              if (!isSubmitting) {
                setSelectedRow(e.value as EntryListRow | null);
              }
            }}
            dataKey="__index"
            scrollable
            scrollHeight="50vh"
            emptyMessage={t('noRecordsDataset')}
          >
            {displayFields.map((field) => (
              <Column
                key={field.name}
                columnKey={field.name}
                header={
                  <InputLabel
                    className="list-entry-column-label"
                    hint={field.hint}
                  >
                    {field.label}
                  </InputLabel>
                }
                body={(rowData: EntryListRow) =>
                  formatEntryListCell(rowData.__row, field, fields)
                }
                bodyClassName={
                  field.alignment ? `cell_${field.alignment}` : undefined
                }
                style={field.width ? { width: field.width } : undefined}
              />
            ))}
          </DataTable>
        </div>
      ) : (
        <div className="list-entry-unsupported">
          {t('entryError.unsupportedList')}
        </div>
      )}
      <EntryActionButtons
        {...(rows
          ? {
              onConfirm: submit,
              confirmDisabled: selectedRow === null || isSubmitting,
            }
          : {})}
        onCancel={() => handleAction(cancelModalAction)}
      />
    </div>
  );
};

export default ListEntryContent;
